import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import Button from '../Button'
import { Download32, InformationFilled32 } from '@carbon/icons-react'
import { validateSchema } from './validators'
import {
  factoryMap,
  CUSTOM,
  HORIZONTAL,
  VERTICAL,
  INLINE_TEXT
} from './constant'
import { InlineLoading } from '../Loading'
import cn from 'classnames'
import './index.scss'

const FactoryKPIS = (props) => {
  const {
    type,
    children,
    headerTitle,
    isLoading = false,
    isDataEmpty = false,
    isError = false,
    noDataLabel = 'NO_DATA',
    className = '',
    export: exportConf,
    infoButton = {},
    ...elementProps
  } = props

  const { enabled: exportEnabled = false, onExport: onExportProp } = exportConf
  const { enabled: infoButtonEnabled = false, message: infoButtonMessage } =
    infoButton
  const showInfoButton = infoButtonEnabled && !!infoButtonMessage

  // Check for the element and the validation schema, given the type
  const { Element, Schema, download } = useMemo(
    () => factoryMap[type] ?? {},
    [type]
  )

  // The given props are validated based on the type
  const error = useMemo(() => validateSchema(Schema, props), [Schema, props])

  const onExport = useCallback(() => {
    const funProps = { ...elementProps, headerTitle }

    if (onExportProp) {
      return onExportProp(funProps)
    }
    return download(funProps)
  }, [onExportProp, download, elementProps, headerTitle])

  // If the props are not validated, return null
  if (error) {
    console.error(error)
    return null
  }

  // If the type does not exists, return null
  if (!Element && type !== CUSTOM) {
    console.error(
      `The type you provided (${type}) is not supported. Please, choose one of the following: ${Object.keys(
        factoryMap
      ).join(', ')}`
    )
    return null
  }

  return (
    <div
      className={cn('kpi-container box-body', {
        'inline-KPI': type === INLINE_TEXT,
        className
      })}
    >
      {(headerTitle || exportEnabled || showInfoButton) && (
        <div className={cn('header', { 'header--no-title': !headerTitle })}>
          {headerTitle && <div className='header__title'>{headerTitle}</div>}
          <div className='header__actions'>
            {showInfoButton && (
              <Button
                hasIconOnly
                className={cn(
                  'header__actions__btn',
                  'header__actions__btn--info',
                  {
                    'header__actions__btn--last': !exportEnabled,
                    'header__actions__btn--first': exportEnabled
                  }
                )}
                renderIcon={InformationFilled32}
                iconDescription={infoButtonMessage}
                tooltipPosition='bottom'
              />
            )}
            {exportEnabled && (
              <Button
                hasIconOnly
                className='header__actions__btn header__actions__btn--export'
                renderIcon={Download32}
                onClick={onExport}
                iconDescription='Download'
                tooltipPosition='bottom'
              />
            )}
          </div>
        </div>
      )}
      <div
        className={cn('box-content', {
          'status-box': isLoading || isError || isDataEmpty
        })}
      >
        {isLoading ? (
          <InlineLoading />
        ) : isError || isDataEmpty ? (
          noDataLabel
        ) : type === CUSTOM ? (
          children
        ) : (
          <Element {...elementProps} />
        )}
      </div>
    </div>
  )
}

const dataObject = PropTypes.shape({
  title: PropTypes.string,
  value: PropTypes.oneOfType[(PropTypes.string, PropTypes.number)]
})

FactoryKPIS.propTypes = {
  headerTitle: PropTypes.string,
  type: PropTypes.string.isRequired,
  export: PropTypes.shape({
    enabled: PropTypes.bool,
    onExport: PropTypes.func
  }),
  data: PropTypes.oneOfType([
    dataObject,
    PropTypes.arrayOf(dataObject),
    PropTypes.any
  ]).isRequired,
  config: PropTypes.shape({
    pipe: PropTypes.bool,
    direction: PropTypes.oneOf([HORIZONTAL, VERTICAL]),
    exportBtn: PropTypes.bool,
    columnsDef: PropTypes.arrayOf(
      PropTypes.shape({ key: PropTypes.string, header: PropTypes.string })
    )
  })
}

FactoryKPIS.defaultProps = {
  export: {
    enabled: false,
    onExport: null
  }
}

export default FactoryKPIS
